import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { ColorDot, COLOR_LABELS, ACTIONABLE_COLORS } from './colorLabels';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PersistenciaListas = ({ token, instances }) => {
  const [snapshots, setSnapshots] = useState([]);
  const [filters, setFilters] = useState({ snapshotId: '', instance: '', color: 'orange', semana: '1' });
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadSnapshots = useCallback(async () => {
    const response = await fetch(`${API_URL}/api/persistencia/snapshots`, { headers: { Authorization: `Bearer ${token}` } });
    const d = await response.json();
    setSnapshots(d.snapshots || []);
    if (d.snapshots?.length > 0) setFilters((f) => ({ ...f, snapshotId: d.snapshots[0].snapshot_id }));
  }, [token]);

  useEffect(() => { loadSnapshots(); }, [loadSnapshots]);

  const handleGenerate = async () => {
    if (!filters.snapshotId || !filters.instance) {
      toast.error('Elegí snapshot e instancia');
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams({
        snapshot_id: filters.snapshotId,
        instance: filters.instance,
        color: filters.color,
        semana: filters.semana
      });
      const response = await fetch(`${API_URL}/api/persistencia/listas-preview?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await response.json();
      if (!response.ok) throw new Error(d.detail || 'Error generando la vista previa');
      setPreview(d);
      if (d.status === 'no_approved_action') toast.info(d.message);
    } catch (error) {
      toast.error(error.message);
      setPreview(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-semibold text-gray-900">Listas (Vista Previa)</h3>
        <p className="text-sm text-gray-500">Alumnos en "Acción" (excluye Control) para un Color+Semana con acción APROBADA, con el mensaje ya resuelto.</p>
      </div>

      <Card>
        <CardContent className="pt-4 grid grid-cols-1 md:grid-cols-5 gap-2">
          <select className="border rounded-md px-3 py-2 text-sm" value={filters.snapshotId} onChange={(e) => setFilters({ ...filters, snapshotId: e.target.value })} data-testid="listas-snapshot-select">
            <option value="">Snapshot...</option>
            {snapshots.map((s) => <option key={s.snapshot_id} value={s.snapshot_id}>{s.snapshot_date} {s.created_at ? new Date(s.created_at).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', hour12: false }) : ''} ({s.total_students} alumnos)</option>)}
          </select>
          <select className="border rounded-md px-3 py-2 text-sm" value={filters.instance} onChange={(e) => setFilters({ ...filters, instance: e.target.value })} data-testid="listas-instance-select">
            <option value="">Instancia...</option>
            {(instances || []).map((i) => <option key={i.slug} value={i.slug}>{i.name}</option>)}
          </select>
          <select className="border rounded-md px-3 py-2 text-sm" value={filters.color} onChange={(e) => setFilters({ ...filters, color: e.target.value })} data-testid="listas-color-select">
            {ACTIONABLE_COLORS.map((c) => <option key={c} value={c}>{COLOR_LABELS[c]}</option>)}
          </select>
          <input
            className="border rounded-md px-3 py-2 text-sm"
            placeholder="Semana"
            value={filters.semana}
            onChange={(e) => setFilters({ ...filters, semana: e.target.value })}
            data-testid="listas-semana-input"
          />
          <Button onClick={handleGenerate} disabled={loading} data-testid="listas-generate-btn">
            {loading ? 'Generando...' : 'Generar vista previa'}
          </Button>
        </CardContent>
      </Card>

      {preview && (
        <Card data-testid="listas-preview-result">
          <CardHeader className="pb-3">
            <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-3 flex items-center gap-2" data-testid="listas-no-send-disclaimer">
              <AlertTriangle className="h-5 w-5 text-yellow-600 shrink-0" />
              <p className="text-sm font-bold text-yellow-800">
                {preview.disclaimer || 'ESTO NO SE ENVÍA - es una vista previa'}
              </p>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {preview.status === 'no_approved_action' ? (
              <p className="text-sm text-gray-500" data-testid="listas-no-approved-message">{preview.message}</p>
            ) : (
              <>
                <div className="flex items-center gap-2 text-sm">
                  <ColorDot color={filters.color} />
                  <span>{COLOR_LABELS[filters.color]} · Semana {filters.semana}</span>
                  <Badge variant="outline">{preview.total_accion} alumnos en Acción</Badge>
                  {preview.truncated && (
                    <span className="text-xs text-amber-600" data-testid="listas-truncated-note">
                      Mostrando {preview.students.length} de {preview.total_accion}
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-600"><strong>Objetivo:</strong> {preview.accion?.objetivo}</p>
                {preview.accion?.asunto && (
                  <p className="text-sm text-gray-600"><strong>Asunto:</strong> {preview.accion.asunto}</p>
                )}
                <div className="max-h-80 overflow-y-auto border rounded-lg divide-y">
                  {preview.students.map((s) => (
                    <div key={s.student_id} className="p-3 text-sm" data-testid={`listas-student-${s.student_id}`}>
                      <p className="font-medium">{s.name} <span className="text-gray-400">({s.email})</span></p>
                      <p className="text-gray-500 mt-1 whitespace-pre-wrap">{s.mensaje_resuelto}</p>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PersistenciaListas;
